import React from 'react';
import { Card, Col, Row, Typography, theme } from 'antd';
import type { PvpLiveDto } from '@/types/pvp';
import {
  TILE_CHECKS,
  capacityLine,
  fmtNum,
  fmtPct,
  peakLine,
  reasonsLine,
  serversLine,
  serversValue,
  tileTone,
  toneColor,
  type LiveTileKey,
} from './model';

const { Text } = Typography;

interface Tile {
  key: LiveTileKey;
  label: string;
  value: string;
  line: string;
}

/**
 * The five headline numbers. A tile's border takes the worst status of its
 * mapped checks (warning or error colour), and the checks behind a coloured
 * border are named under it, so colour is never the only sign.
 */
export const LiveTiles: React.FC<{ dto: PvpLiveDto }> = ({ dto }) => {
  const { token } = theme.useToken();
  const tones = tileTone(dto);
  const { now, matches } = dto;
  const tiles: Tile[] = [
    { key: 'online', label: 'Online now', value: fmtNum(now.ccu, 0), line: peakLine(now) },
    {
      key: 'liveMatches',
      label: 'Live matches',
      value: fmtNum(now.liveMatches, 0),
      line: capacityLine(now),
    },
    {
      key: 'done',
      label: 'Matches done, last hour',
      value: fmtNum(matches.lastHour.done, 0),
      line: `today ${fmtNum(matches.today.done, 0)}`,
    },
    {
      key: 'completion',
      label: 'Completion, last hour',
      value: fmtPct(matches.lastHour.completionPct),
      line: reasonsLine(matches.lastHour),
    },
    { key: 'servers', label: 'Servers', value: serversValue(now), line: serversLine(now) },
  ];

  return (
    <Row gutter={[12, 12]}>
      {tiles.map((tile) => {
        const tone = tones[tile.key];
        const color = toneColor(tone, { warning: token.colorWarning, error: token.colorError });
        const flagged =
          tone === 'warn' || tone === 'bad'
            ? dto.checks
                .filter((c) => TILE_CHECKS[tile.key].includes(c.key) && c.status === tone)
                .map((c) => c.label)
            : [];
        return (
          <Col key={tile.key} flex="1 1 190px">
            <div data-tile={tile.label} data-tone={tone} style={{ height: '100%' }}>
              <Card
                size="small"
                style={{
                  height: '100%',
                  ...(color ? { borderColor: color, borderWidth: 2 } : {}),
                }}
              >
                <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                  {tile.label}
                </Text>
                <Text strong style={{ fontSize: 24, fontVariantNumeric: 'tabular-nums' }}>
                  {tile.value}
                </Text>
                <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                  {tile.line}
                </Text>
                {flagged.length > 0 && (
                  <Text
                    type={tone === 'bad' ? 'danger' : 'warning'}
                    style={{ fontSize: 12, display: 'block', marginTop: 2 }}
                  >
                    {tone === 'bad' ? 'Bad' : 'Warning'}: {flagged.join(', ')}
                  </Text>
                )}
              </Card>
            </div>
          </Col>
        );
      })}
    </Row>
  );
};
